import {
  ArApType,
  ENDPOINTS,
  InvoiceStatusId,
  PAGE_SIZE_DEFAULT,
  PAGE_SIZE_DEFAULT_MAX,
  ParentSource,
  QUERY_KEYS
} from '@constants';
import { Prototype } from '@core';
import APIManager from '@services';
import { useQueries } from '@tanstack/react-query';

import { useInfiniteEnhancer, useQueryEnhancer } from '../core';

interface IParallelQueriesProps {
  keys: any[];
  api?: string;
  fn?: () => Promise<any>;
  enabled?: boolean;
}

export const NOW = Prototype.date.now();

export const useFetchDashboardIssuesEquipment = (
  date: string | null,
  enabled?: boolean
) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardIssuesEquipment, date],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.equipment('dashboard'),
        body: {
          date
        }
      });

      return res.data ?? [];
    },
    enabled
  });
};

export const useFetchDashboardEquipment = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardEquipment],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.equipment('dashboard/count'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });

      return res.data;
    }
  });
};

export const useFetchDashboardIssuesDriver = (
  date: string | null,
  enabled?: boolean
) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardIssuesDriver, date],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.staff('dashboard'),
        body: {
          date
        }
      });

      return res.data ?? [];
    },
    enabled
  });
};

export const useFetchDashboardDrivers = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardDrivers],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.staff('dashboard/drivers'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });
      return res.data;
    }
  });
};

export const useFetchDashboardIssuesClientRate = (
  date: string | null,
  enabled?: boolean
) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardIssuesClientRate, date],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.clientRate('dashboard'),
        body: {
          date
        }
      });

      return res.data;
    },
    enabled
  });
};

export const useFetchDashboardTransportJobs = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardTransportJobs],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.transportJob('dashboard'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });

      return res.data;
    }
  });
};

export const useFetchDashboardContainers = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardContainers],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.transportJob('job-container/dashboard'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });

      return res.data;
    }
  });
};

export const useFetchDashboardPayments = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardPayments],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.payment('dashboard'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });

      return res.data;
    }
  });
};

export const useFetchDashboardPaymentStatistic = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardPaymentStatistic],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.payment('statistic'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });

      return res.data;
    }
  });
};

export const useFetchDashboardInvoicesOverdue = () => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardInvoicesOverdue],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.invoice(),
        body: {
          excludeFields: ['invoiceContainerCharges'],
          invoiceProgressId: InvoiceStatusId.Invoiced,
          size: PAGE_SIZE_DEFAULT_MAX,
          filterDueDate: Prototype.date.formatDB(NOW)
        }
      });

      return res.data?.data ?? [];
    },
    initialData: []
  });
};

export const useFetchDashboardAROverdue = (
  searchField?: string,
  searchValue?: string
) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAROverdue, searchField, searchValue],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.statement('ar-client-summary'),
        body: {
          extraFields: ['creditLimit'],
          size: PAGE_SIZE_DEFAULT,
          ...(!searchField && searchValue ? { keyword: searchValue } : {}),
          searchCondition: JSON.stringify([
            {
              fieldName: 'overdue',
              operandType: 'DECIMAL',
              operatorType: 'GREATER_EQUAL',
              data: '0.01'
            },
            ...(searchField && searchValue
              ? [
                  {
                    fieldName: searchField,
                    operandType: 'STRING',
                    operatorType: 'CONTAIN',
                    data: searchValue
                  }
                ]
              : [])
          ])
        }
      });

      return res.data?.data ?? [];
    }
    // initialData: []
  });
};

export const useFetchDashboardAROverdueInfinity = (
  searchField?: string,
  searchValue?: string
) => {
  return useInfiniteEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAROverdue, searchField, searchValue],
    queryFn: async ({ pageParam }) => {
      const { page = 0, pageSize = PAGE_SIZE_DEFAULT } = pageParam || {};
      const res = await APIManager.request({
        url: ENDPOINTS.statement('ar-client-summary'),
        body: {
          extraFields: ['creditLimit'],
          page,
          size: pageSize,
          ...(!searchField && searchValue ? { keyword: searchValue } : {}),
          searchCondition: JSON.stringify([
            {
              fieldName: 'overdue',
              operandType: 'DECIMAL',
              operatorType: 'GREATER_EQUAL',
              data: '0.01'
            },
            ...(searchField && searchValue
              ? [
                  {
                    fieldName: searchField,
                    operandType: 'STRING',
                    operatorType: 'CONTAIN',
                    data: searchValue
                  }
                ]
              : [])
          ])
        }
      });

      return res.data ?? [];
    }
    // initialData: []
  });
};

export const useFetchDashboardAPOverdue = (
  searchField?: string,
  searchValue?: string
) => {
  return useInfiniteEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAPOverdue, searchField, searchValue],
    queryFn: async ({ pageParam }) => {
      const { page = 0, pageSize = PAGE_SIZE_DEFAULT } = pageParam || {};
      const res = await APIManager.request({
        url: ENDPOINTS.statement('ap-client-summary'),
        body: {
          extraFields: ['creditLimit'],
          page,
          size: pageSize,
          ...(!searchField && searchValue ? { keyword: searchValue } : {}),
          searchCondition: JSON.stringify([
            {
              fieldName: 'overdue',
              operandType: 'DECIMAL',
              operatorType: 'GREATER_EQUAL',
              data: '0.01'
            },
            ...(searchField && searchValue
              ? [
                  {
                    fieldName: searchField,
                    operandType: 'STRING',
                    operatorType: 'CONTAIN',
                    data: searchValue
                  }
                ]
              : [])
          ])
        }
      });

      return res.data?.data ?? [];
    }
  });
};

export const useFetchDashboardAPPayment = (otherParams?: any) => {
  return useInfiniteEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAPPayment, JSON.stringify(otherParams)],
    queryFn: async ({ pageParam }) => {
      const { page = 0, pageSize = PAGE_SIZE_DEFAULT } = pageParam || {};
      const res = await APIManager.request({
        url: ENDPOINTS.payment(),
        body: {
          accountingType: ArApType.Ap,
          size: pageSize,
          page,
          ...otherParams
        }
      });

      return res.data ?? [];
    }
  });
};

export const useFetchDashboardARPayment = (otherParams?: any) => {
  return useInfiniteEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardARPayment, JSON.stringify(otherParams)],
    queryFn: async ({ pageParam }) => {
      const { page = 0, pageSize = PAGE_SIZE_DEFAULT } = pageParam || {};
      const res = await APIManager.request({
        url: ENDPOINTS.payment(),
        body: {
          accountingType: ArApType.Ar,
          size: pageSize,
          page,
          ...otherParams
        }
      });

      return res.data ?? [];
    }
  });
};

export const useFetchDashboardAccountingSummary = () => {
  const date = Prototype.date.formatDB(NOW);

  const queries: IParallelQueriesProps[] = [
    {
      keys: [QUERY_KEYS.dashboardARClient],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.statement('ar-client-summary/dashboard')
        });

        return res.data;
      }
    },
    {
      keys: [QUERY_KEYS.dashboardARInvoice],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.transportJob('invoice/dashboard'),
          body: {
            date,
            isAR: true
          }
        });

        return res.data;
      }
    },
    {
      keys: [QUERY_KEYS.dashboardClientRate],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.clientRate('auto-rate/dashboard'),
          body: {
            date
          }
        });

        return res.data;
      }
    },
    {
      keys: [QUERY_KEYS.dashboardAPClient],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.statement('ap-client-summary/dashboard')
        });

        return res.data;
      }
    },
    {
      keys: [QUERY_KEYS.dashboardAPInvoice],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.transportJob('invoice/dashboard'),
          body: {
            date,
            isAR: false
          }
        });

        return res.data;
      }
    },
    {
      keys: [QUERY_KEYS.dashboardPaymentChart],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.payment('dashboard/payment-charts'),
          body: {
            date,
            isAR: false
          }
        });

        return res.data;
      }
    }
  ];

  const [
    arClientQuery,
    arInvoiceQuery,
    clientRateQuery,
    apClientQuery,
    apInvoiceQuery,
    paymentChartQuery
  ] = useQueries({
    queries: queries.map(q => {
      return {
        queryKey: q.keys,
        queryFn: q.fn
      };
    })
  });

  return {
    arClientQuery,
    arInvoiceQuery,
    clientRateQuery,
    apClientQuery,
    apInvoiceQuery,
    paymentChartQuery
  };
};

export const useFetchDashboardAllocationReports = (
  date: string | null,
  keyword?: string
) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAllocationReports, date, keyword],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.report(),
        body: {
          filterDriverReport: false,
          keyword,
          reportDate: date,
          size: PAGE_SIZE_DEFAULT_MAX,
          source: ParentSource.Allocation
        }
      });

      return res.data?.data ?? [];
    },
    initialData: []
  });
};

export const useFetchDashboardAllocationReportInfinity = (
  date: string | null,
  keyword?: string
) => {
  return useInfiniteEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAllocationReports, date, keyword],
    queryFn: async ({ pageParam }) => {
      const { page = 0, pageSize = PAGE_SIZE_DEFAULT } = pageParam || {};
      const res = await APIManager.request({
        url: ENDPOINTS.report(),
        body: {
          filterDriverReport: false,
          keyword,
          page,
          size: pageSize,
          reportDate: date,
          source: ParentSource.Allocation
        }
      });
      return res.data ?? [];
    }
  });
};

export const useFetchDashboardAllocationMessages = (date: string | null) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardAllocationMessages, date],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.conversation('conversation-message/find-unread'),
        body: {
          date
        }
      });

      return res.data ?? [];
    },
    initialData: []
  });
};

export const useFetchDashboardSTCWaitingTime = (date: string | null) => {
  return useQueryEnhancer<any[]>({
    queryKey: [QUERY_KEYS.dashboardScheduleTripContainer, date],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.scheduleTripContainer('dashboard'),
        body: {
          page: 0,
          size: PAGE_SIZE_DEFAULT_MAX,
          date
        }
      });

      return res.data ?? [];
    },
    initialData: []
  });
};

export const useFetchDashboardTrips = () => {
  return useQueryEnhancer<any>({
    queryKey: [QUERY_KEYS.dashboardTrips],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.trip('dashboard'),
        body: {
          date: Prototype.date.formatDB(NOW)
        }
      });

      return res.data;
    }
  });
};

export const useFetchDashboardOperationSummary = () => {
  const date = Prototype.date.formatDB(NOW);

  const queries: IParallelQueriesProps[] = [
    {
      keys: [QUERY_KEYS.dashboardTransportJobs, date],
      api: ENDPOINTS.transportJob('dashboard')
    },
    {
      keys: [QUERY_KEYS.dashboardContainers, date],
      api: ENDPOINTS.transportJob('job-container/dashboard')
    },
    {
      keys: [QUERY_KEYS.dashboardTrips, date],
      api: ENDPOINTS.trip('dashboard')
    },
    {
      keys: [QUERY_KEYS.dashboardDrivers, date],
      api: ENDPOINTS.staff('dashboard/drivers')
    },
    {
      keys: [QUERY_KEYS.dashboardEquipment, date],
      fn: async () => {
        const res = await APIManager.request({
          url: ENDPOINTS.equipment('dashboard/count'),
          body: {
            date
          }
        });
        const { data } = res;
        return {
          numOfEquipmentFaulty: data?.numOfFaulty,
          numOfEquipmentInGoodCondition: data?.numOfInGoodCondition,
          numOfEquipmentInRepair: data?.numOfInRepair,
          numOfEquipmentLicenseValid: data?.numOfLicenseValid,
          numOfEquipmentNoRegistration: data?.numOfNoRegistration,
          numOfEquipmentRegistrationExpired: data?.numOfRegistrationExpired
        };
      }
    }
  ];

  const [
    transportJobQuery,
    containerQuery,
    tripQuery,
    driverQuery,
    equipmentQuery
  ] = useQueries({
    queries: queries.map(api => {
      const defaultFn = async () => {
        const res = await APIManager.request({
          url: api.api ?? '',
          body: {
            date
          }
        });

        return res.data;
      };
      return {
        queryKey: api.keys,
        queryFn: api?.fn ?? defaultFn
      };
    })
  });

  return {
    transportJobQuery,
    containerQuery,
    tripQuery,
    driverQuery,
    equipmentQuery
  };
};

export const useFetchDashboardOperationTripSummary = (
  date: string,
  driverIds: (string | number)[]
) => {
  return useQueryEnhancer({
    queryKey: [QUERY_KEYS.dashboardTripSummary, date, driverIds],
    queryFn: async () => {
      const res = await APIManager.request({
        url: ENDPOINTS.scheduleTrip('dashboard'),
        body: {
          date,
          driverIdIn: driverIds
        }
      });
      return res.data;
    },
    enabled: !!date
  });
};

export const useFetchDashboardByClient = ({
  clientIds,
  relatedClientType,
  calculatedBy
}: {
  clientIds: number[];
  relatedClientType: string | null;
  calculatedBy: string | null;
}) => {
  return useQueryEnhancer<any>({
    queryKey: [
      QUERY_KEYS.dashboardClientStatistic,
      clientIds,
      relatedClientType,
      calculatedBy
    ],
    queryFn: async (): Promise<any> => {
      const res = await APIManager.request({
        url: ENDPOINTS.transportJob('invoice/dashboard/by-client'),
        body: {
          clientIdIn: clientIds,
          relatedClientType,
          calculationType: calculatedBy,
          isAR: true
        }
      });
      return {
        statistic: res.data?.data ?? [],
        totalOverdue: res.data?.totalOverdue ?? 0,
        totalInvoices: res.data?.totalInvoice ?? 0
      };
    },
    enabled: !!(clientIds.length > 0 && relatedClientType && calculatedBy)
  });
};
